/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Injectable } from '@angular/core';
import { ModalRef, ModalService } from '@wfm/core';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { AssignmentConfirmationComponent } from '@app/common/modals/components/assignment-confirmation/assignment-confirmation.component';
import { TranslateService } from '@ngx-translate/core';
import { Logger, MessageService } from '@core/lib/services';
import { Subscription } from 'rxjs';
import { ToDoCountService } from '@app/ui/common/work-list-navigator/to-do-count.service';
import { AssignExamComponent } from '@app/common/modals/components/assign-exam/assign-exam.component';
import { PatientConfidentialComponent } from '@app/common/modals/components/patient-confidential/patient-confidential.component';
import { UserPreferenceComponent } from '@app/common/modals/components/user-preference/user-preference.component';
import { MergePatientComponent } from '@app/ui/merge-patient/merge-patient.component';
import { AuditTrailComponent } from '@app/common/modals/components/audit-trail/audit-trail.component';
import { AdvanceSearchComponent } from '@app/common/modals/components/advance-search/advance-search.component';
import { DatabaseList } from '@app/data-model/list/database-list';
import { RecordManagementComponent } from '@wfm/record-management';
import { UtilService } from '@app/util/util.service';
import { AppConfigService } from '@app/common/app-config.service';

const log = new Logger('ModalsService');

@Injectable({
  providedIn: 'root'
})
export class ModalsService {
  constructor(
    private modalService: ModalService,
    private translate: TranslateService,
    private toDoCountService: ToDoCountService,
    private userPreferenceService: UserPreferenceService,
    private utilService: UtilService,
    private messageService: MessageService,
    private appConfigService: AppConfigService
  ) {}

  openWorkflowPreferences() {
    this.modalService.openModal(UserPreferenceComponent, {
      headerContent: this.translate.instant('userPreference.workflowPreferences'),
      modalClass: 'modal--medium'
    });
  }

  openUnAssignModal(preferenceKey: string, skipTabClosurePopup: boolean, params?: any) {
    const preference = this.userPreferenceService.getUserPreferenceFromCache(preferenceKey);

    return new Promise((resolve, reject) => {
      if (preference === 'decide') {
        if (skipTabClosurePopup) {
          resolve(false);
        } else {
          let assignExamsCount = 0;
          if (params && params.list) {
            assignExamsCount = params.list.length;
          } else if (this.toDoCountService.getAssignedCount()) {
            assignExamsCount = this.toDoCountService.getAssignedCount().total;
          }

          let title = '';
          let content = '';
          if (preferenceKey === 'unassign-on-tab-closure') {
            if (params && params.action === 'closeAll') {
              title = this.translate.instant('assignments.closeAllExamMsg');
              content = this.translate.instant('assignments.releaseAllExamsBeforeCloseMsg');
            } else {
              title = this.translate.instant('assignments.closeExamMsg');
              content = this.translate.instant('assignments.releaseExamsBeforeCloseMsg');
            }
          } else if (params && params.action && params.action === 'assign') {
            title = this.translate.instant('assignments.assignmentsMsg');
            content = this.translate.instant('assignments.examsSelectionWarningMsg');
          } else {
            title = this.translate.instant('assignments.assignmentsMsg');
            content = [
              this.translate.instant('assignments.youHaveMsg'),
              assignExamsCount,
              this.translate.instant('assignments.examsAssingnedMsg'),
              this.translate.instant('assignments.wantToReleaseMsg')
            ].join(' ');
          }

          const modalRef = this.modalService.openModal(AssignmentConfirmationComponent, {
            headerContent: title,
            data: {
              content,
              showPreferenceOption: true,
              action: params && params.action
            }
          });
          const subscription: Subscription =
            modalRef &&
            modalRef.afterClosed &&
            modalRef.afterClosed.subscribe(data => {
              if (data && data.result) {
                resolve(data);
              } else {
                reject(data);
              }
              if (data && data.rememberPref) {
                log.info('Update user preference : ' + preferenceKey);
                this.userPreferenceService.createOrUpdateUserPreference(
                  preferenceKey,
                  data.result ? 'release' : 'keep'
                );
              }
              subscription.unsubscribe();
            });
        }
      } else if (preference === 'release') {
        resolve(true);
      } else if (preference === 'keep') {
        reject({ result: false });
      } else {
        log.error(preferenceKey + ' is not supported user preference key.');
        throw new Error("Unsupported user preference for key '" + preferenceKey + "'!");
      }
    });
  }

  openAssignExamModal(item: any) {
    return this.modalService.openModal(AssignExamComponent, {
      headerContent: this.translate.instant('label.assign'),
      data: {
        item: item
      }
    });
  }

  openPatientFlagDialog(workItem: any, confidentialFlag: boolean, auditInfo: any) {
    return this.modalService.openModal(PatientConfidentialComponent, {
      headerContent: confidentialFlag
        ? this.translate.instant('context.patientConfidentialStatusUpdateTitle')
        : this.translate.instant('context.patientNonConfidentialStatusUpdateTitle'),
      data: {
        patientId: workItem.patientId,
        showConfidential: confidentialFlag,
        auditInfo: auditInfo
      }
    });
  }

  openMergePatientModal(item: any) {
    return this.modalService.openModal(MergePatientComponent, {
      headerContent: this.translate.instant('mergePatient.label'),
      modalClass: 'modal--large',
      data: {
        item: item
      }
    });
  }

  openAuditTrailModal(workItem: any) {
    return this.modalService.openModal(AuditTrailComponent, {
      headerContent: this.translate.instant('workloadManager.listContainer.workItem.auditTrails'),
      modalClass: 'modal-xlarge',
      data: {
        item: workItem
      }
    });
  }

  openAdvanceSearchModal(type: string, list?: DatabaseList) {
    const headerLabel =
      type === 'createWL'
        ? 'advancedSearch.modalTitle.create'
        : type === 'editWL'
        ? 'advancedSearch.modalTitle.edit'
        : 'advancedSearch.modalTitle.search';
    return this.modalService.openModal(AdvanceSearchComponent, {
      headerContent: this.translate.instant(headerLabel),
      modalClass: 'modal--large',
      data: {
        type,
        list
      }
    });
  }

  openRecordManagementModal(data: any, size: string) {
    return this.modalService.openModal(RecordManagementComponent, {
      headerContent: 'Record Management',
      modalClass: size,
      data: data
    });
  }

  // Modal showing Record Not Found stale tab
  showRecordNotFoundAlertMessage(): void {
    const cpacsVersion = this.appConfigService.getAppConfigByKey('cpacsVersion');
    const modalConfig = {
      v7: {
        message: this.translate.instant('examMerged.update.message'),
        header: this.translate.instant('examMerged.update.header')
      },
      v8: {
        message: this.translate.instant('recordMerged.update.message'),
        header: this.translate.instant('recordMerged.update.header')
      }
    };
    const config = modalConfig[cpacsVersion];
    const modalRef = this.modalService.openModal(config.message, {
      headerContent: config.header,
      isDecisionModal: false,
      disableHeaderCloseBtn: true,
      yesButtonLabel: this.translate.instant('button.ok')
    });

    modalRef.afterClosed.subscribe(() => {
      this.messageService.broadcast('removeRecordNotFoundTab', this.utilService.getActiveTab().id);
      this.messageService.broadcast('closeDiscardForExamNotExists', this.utilService.getActiveTab());
    });
  }

  // Modal showing update required for stale tab
  showStaleTabAlertMessage(): void {
    this.modalService.openModal(this.translate.instant('exam.update.message').replace(/\n/g, '<br>'), {
      headerContent: this.translate.instant('exam.update.header'),
      enableYesBtn: false,
      isDecisionModal: false,
      disableHeaderCloseBtn: true,
      noButtonLabel: this.translate.instant('button.ok')
    });
  }

  /**
   * Method to open Microapp Shell with given data
   * @param data - consisting of `appId: string`, `url: string` to open in an iframe, `sessionId: string` if any
   * @returns `Promise<ModalRef>` - A boolean indicating whether the modal is visible or hidden.
   */
  async openMicroAppModal(data: any): Promise<ModalRef> {
    const { MicroAppShellComponent } = await import(
      '@app/di/channel/microapp/micro-app-shell/micro-app-shell.component'
    );
    return this.modalService.openModal(MicroAppShellComponent, {
      data,
      modalClass: 'modal--xlarge'
    });
  }
  async openDaErrorModal(type: 'window' | 'health' | 'syncError'): Promise<'YES' | 'NO'> {
    let headerContent = '';
    let description = '';
    let yesButtonLabel = this.translate.instant('label.yes');
    let noButtonLabel = this.translate.instant('label.no');
    if (type === 'window') {
      headerContent = this.translate.instant('da.restartModal.header');
      description = this.translate.instant('da.restartModal.description');
    } else if (type === 'health') {
      headerContent = this.translate.instant('da.disconnected.header');
      description = this.translate.instant('da.restartModal.description');
    } else {
      headerContent = this.translate.instant('da.error.block.header');
      description = this.translate.instant('da.error.block.description');
      yesButtonLabel = this.translate.instant('da.error.block.button.label.restartDigitalAssistant');
      noButtonLabel = this.translate.instant('da.error.block.button.label.dismiss');
    }
    return this.modalService
      .openModal(description, {
        headerContent,
        disableHeaderCloseBtn: true,
        enableYesBtn: true,
        isDecisionModal: true,
        showAlertIcon: true,
        modalClass: type === 'syncError' ? 'modal--medium' : 'modal',
        noButtonLabel,
        yesButtonLabel
      })
      .afterClosed.toPromise();
  }

  /**
   * Method to open EV Error Modal for window/health/ retry event scenarios
   * @param type - `'window' | 'health' | 'syncError'`
   * @returns `Promise<'YES'| 'NO'>` - A String 'YES' or 'NO' based on users action
   */
  async openEvErrorModal(type: 'window' | 'health' | 'syncError'): Promise<'YES' | 'NO'> {
    let headerContent = '';
    let description = '';
    let yesButtonLabel = this.translate.instant('label.yes');
    let noButtonLabel = this.translate.instant('label.no');
    if (type === 'window') {
      headerContent = this.translate.instant('ev.restartModal.header');
      description = this.translate.instant('ev.restartModal.description');
    } else if (type === 'health') {
      headerContent = this.translate.instant('ev.disconnected.header');
      description = this.translate.instant('ev.restartModal.description');
    } else {
      headerContent = this.translate.instant('ev.error.block.header');
      description = this.translate.instant('ev.error.block.description');
      yesButtonLabel = this.translate.instant('ev.error.block.button.label.restartViewer');
      noButtonLabel = this.translate.instant('ev.error.block.button.label.dismiss');
    }
    return this.modalService
      .openModal(description, {
        headerContent,
        disableHeaderCloseBtn: true,
        enableYesBtn: true,
        isDecisionModal: true,
        showAlertIcon: true,
        modalClass: type === 'syncError' ? 'modal--medium' : 'modal',
        noButtonLabel,
        yesButtonLabel
      })
      .afterClosed.toPromise();
  }
}
